import { useMemo, useState } from 'react';
import type { GameData } from '@library/types/games';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { LeagueInfo } from '@library/types/leagues';
import type { LeagueTeamPlayer } from '@library/types/player';
import type { SimulationRun } from '@library/types/simulations';
import { StepFourPublish } from '@bo-components/simulations/steps/StepFourPublish/StepFourPublish';
import { StepOneBuild } from '@bo-components/simulations/steps/StepOneBuild/StepOneBuild';
import { StepThreeFinalize } from '@bo-components/simulations/steps/StepThreeFinalize/StepThreeFinalize';
import { StepTwoResults } from '@bo-components/simulations/steps/StepTwoResults/StepTwoResults';
import { Stepper } from '@library/components/Stepper/Stepper';
import { StepperNavigation } from '@library/components/Stepper/StepperNavigation';
import { useRequestSimulation } from '@bo-hooks/simulations/useRequestSimulation';
import { useSimulationBuilder } from '@bo-hooks/simulations/useSimulationBuilder';
import { useSimulationDetailSteps } from '@bo-hooks/simulations/useSimulationDetailSteps';
import { useSimulationOdds } from '@bo-hooks/simulations/useSimulationOdds';
import { useSimulationsExistingProjections } from '@bo-hooks/simulations/useSimulationsExistingProjections';
import { useTrackFinalizeProjections } from '@bo-hooks/simulations/useTrackFinalizeProjections';

export interface SimulationStepsProps {
  allMarkets: Array<{
    league_id: string;
    market: string;
    market_id: string;
    stat_type_id: number;
  }>;
  defaultSimulation: SimulationRun;
  game: GameData;
  gamePlayers: Array<LeagueTeamPlayer>;
  isNewSimulation: boolean;
  leagueInfo: LeagueInfo;
  relevantPlayers: Array<LeagueTeamPlayer>;
  simulation?: SimulationRun;
  simulationId: string;
}

export const SimulationSteps = ({
  allMarkets,
  defaultSimulation,
  game,
  gamePlayers,
  isNewSimulation,
  leagueInfo,
  relevantPlayers,
  simulation = undefined,
  simulationId
}: SimulationStepsProps) => {
  const { baseLeagueFilters, id: leagueId } = leagueInfo;
  const currentLeagueFilters = baseLeagueFilters.find(
    (filters) => filters.leagueId === leagueId
  );

  const { durations, statTypes } = currentLeagueFilters ?? {};

  const simulationBuilder = useSimulationBuilder({
    defaultSimulation,
    game,
    gamePlayers: relevantPlayers,
    leagueId,
    simulation
  });

  const [proposedProjections, setProposedProjections] = useState<
    Array<GamePlayerProjectionTrading>
  >([]);

  const { id: gameId, scheduled_at: gameScheduledAt } = game;

  const {
    existingProjections,
    isSimulationExistingProjectionsError,
    isSimulationExistingProjectionsLoading
  } = useSimulationsExistingProjections({
    allMarkets,
    gameId,
    leagueId
  });

  const {
    simulationOdds,
    isSimulationOddsError,
    isSimulationOddsLoading,
    simulationGeneratedProjections
  } = useSimulationOdds({
    allMarkets,
    durations,
    existingProjections: existingProjections || [],
    gameId,
    gameScheduledAt,
    leagueId,
    playersFromRoster: relevantPlayers,
    simulationId: simulationId || '',
    statTypes
  });

  const { handleRequestSimulationRun } = useRequestSimulation({
    gameId: game.id,
    leagueId,
    simulationBuilder
  });

  const {
    navigationSteps,
    overrideValidationErrors,
    publishSimulationStatus,
    step,
    steps,
    setOverrideValidationErrors
  } = useSimulationDetailSteps({
    gameId: game.id,
    handleRequestSimulationRun,
    isNewSimulation,
    leagueId,
    players: relevantPlayers,
    proposedProjections,
    simulation,
    simulationId
  });

  const { newAndChangedProjections, updateProjection } =
    useTrackFinalizeProjections({ setProposedProjections });

  const stepperNavigation = useMemo(() => {
    const { back, currentStep, next } = navigationSteps;
    return (
      <StepperNavigation back={back} currentStep={currentStep} next={next} />
    );
  }, [navigationSteps]);

  return (
    <Stepper steps={steps}>
      {stepperNavigation}
      {step === 1 && (
        <StepOneBuild
          allMarkets={allMarkets}
          game={game}
          gamePlayers={gamePlayers}
          handleRequestSimulationRun={handleRequestSimulationRun}
          leagueId={leagueId}
          overrideValidationErrors={overrideValidationErrors}
          relevantPlayers={relevantPlayers}
          setOverrideValidationErrors={setOverrideValidationErrors}
          simulation={simulation}
          simulationBuilder={simulationBuilder}
        />
      )}
      {step === 2 && (
        <StepTwoResults
          allMarkets={allMarkets}
          game={game}
          gamePlayers={gamePlayers}
          handleRequestSimulationRun={handleRequestSimulationRun}
          leagueId={leagueId}
          isSimulationOddsError={isSimulationOddsError}
          isSimulationOddsLoading={isSimulationOddsLoading}
          overrideValidationErrors={overrideValidationErrors}
          relevantPlayers={relevantPlayers}
          setOverrideValidationErrors={setOverrideValidationErrors}
          simulation={simulation}
          simulationBuilder={simulationBuilder}
          simulationOdds={simulationOdds}
        />
      )}
      {step === 3 && simulation && (
        <StepThreeFinalize
          allMarkets={allMarkets}
          existingProjections={existingProjections}
          isSimulationExistingProjectionsError={
            isSimulationExistingProjectionsError
          }
          isSimulationExistingProjectionsLoading={
            isSimulationExistingProjectionsLoading
          }
          isSimulationOddsError={isSimulationOddsError}
          isSimulationOddsLoading={isSimulationOddsLoading}
          leagueId={leagueId}
          newAndChangedProjections={newAndChangedProjections}
          simulationGeneratedProjections={simulationGeneratedProjections}
          simulationOdds={simulationOdds}
          statTypes={statTypes}
          updateProjection={updateProjection}
        />
      )}
      {simulation && step === 4 && (
        <StepFourPublish
          gameId={game.id}
          leagueId={leagueId}
          publishSimulationStatus={publishSimulationStatus}
          simulation={simulation}
        />
      )}
      {stepperNavigation}
    </Stepper>
  );
};
